import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Button, Paper, PaperProps, Stack } from '@mui/material';
import { capitalize, min, throttle } from 'lodash';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Resizable, ResizableProps } from 'react-resizable';

import 'react-resizable/css/styles.css';

const HANDLE_WIDTH = 6;

export type ResizableSidePanelProps = {
    /**
     * Which side of the window does the panel expand out from
     */
    side: 'left' | 'right';
    /**
     * Pixel width at which we collapse
     */
    minWidth?: number;
    /**
     * Max width at which we stop expanding. If > 1, width measured in pixels, if <= 1, width is fraction of parent width.
     */
    maxWidth?: number;
    /**
     * Pixel width at first render
     */
    initialWidth?: number;
    /**
     * Pixel offset of
     */
    handleOffset?: number;
    collapsedIcon?: ReactNode;
    sx?: PaperProps['sx'];
    children: ReactNode;
};

export const ResizableSidePanel = ({
    side,
    minWidth = 100,
    maxWidth,
    initialWidth = 300,
    collapsedIcon,
    sx,
    children,
}: ResizableSidePanelProps) => {
    const [width, setWidth] = useState(initialWidth);
    const [isResizing, setIsResizing] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const onResize: NonNullable<ResizableProps['onResize']> = (event) => {
        // Calculating target width ourselves rather than using size from the second onResize parameter because
        // for some reason their calculation drifts off reality, especially if dragging incurs rerendering.
        const { clientX } = event as unknown as MouseEvent;
        const container = containerRef.current;
        if (!container) return;

        const maxWidthPx = maxWidth && (maxWidth > 1 ? maxWidth : maxWidth * container.parentElement!.clientWidth);
        const { paddingLeft, paddingRight } = window.getComputedStyle(container);
        const totalPaddingX = Number(paddingLeft.replace(/px$/, '')) + Number(paddingRight.replace(/px$/, ''));
        const offset = totalPaddingX - HANDLE_WIDTH / 2;
        const width = side === 'right' ? window.innerWidth - clientX - offset : clientX - offset;
        setWidth(min([Math.max(width, minWidth - 1), maxWidthPx, window.innerWidth - totalPaddingX - HANDLE_WIDTH])!);
        setIsCollapsed(width < minWidth);
    };

    const dispatchResize = useMemo(
        () =>
            throttle(
                () => {
                    /* suggest the map remeasure */
                    window.dispatchEvent(new Event('resize'));
                },
                50,
                { leading: true, trailing: true }
            ),
        []
    );
    useEffect(() => dispatchResize(), [dispatchResize, width, isCollapsed]);

    const resizeHandleSide = side === 'left' ? 'e' : 'w';
    const capitalSide = useMemo(() => capitalize(side), [side]);

    return (
        <Resizable
            axis="x"
            width={width}
            resizeHandles={[resizeHandleSide]}
            onResize={onResize}
            onResizeStart={() => setIsResizing(true)}
            onResizeStop={() => setIsResizing(false)}
            handle={
                <Box
                    className={`react-resizable-handle-${resizeHandleSide} ${isResizing ? 'resizing' : ''}`}
                    sx={{
                        position: 'absolute',
                        zIndex: 0,
                        my: '4px',
                        top: '2px',
                        height: 'calc(100% - 8px)',
                        width: `${HANDLE_WIDTH}px`,
                        '&:hover': {
                            background: 'linear-gradient(lightgray, lightgray) no-repeat center/2px 100%',
                        },
                        '&.resizing, &:hover.resizing': {
                            background: 'linear-gradient(darkgray, darkgray) no-repeat center/2px 100%',
                        },
                        background: 'none',
                        transform: 'none',
                    }}
                />
            }
        >
            <Paper
                ref={containerRef}
                className={isCollapsed ? 'collapsed' : ''}
                sx={{
                    p: 1,
                    overflowX: 'visible',
                    '&.collapsed': { p: 0 },
                    boxSizing: 'border-box',
                    zIndex: 1,
                    ...sx,
                }}
            >
                <Button
                    sx={{
                        position: 'absolute',
                        borderRadius: 1,
                        top: '32px',
                        [side]: '100%',
                        zIndex: 110,
                        minWidth: '0px',
                        [`margin${capitalSide}`]: '-10px',
                        p: 0,
                        backgroundColor: 'white',
                        color: 'lightgray',
                        '&:hover': { color: 'info.main' },
                        '.collapsed &': {
                            py: '4px',
                            [`margin${capitalSide}`]: 0,
                            [`border${capitalSide}`]: 'none',
                            [`borderTop${capitalSide}Radius`]: 0,
                            [`borderBottom${capitalSide}Radius`]: 0,
                            boxShadow: 'var(--Paper-shadow)',
                        },
                    }}
                    variant="outlined"
                    color="inherit"
                    onClick={() => setIsCollapsed((value) => !value)}
                >
                    <ExpandCollapseButtonIcons side={side} collapsedIcon={collapsedIcon} collapsed={isCollapsed} />
                </Button>
                <Stack
                    sx={{
                        width: `${width}px`,
                        height: 1,
                        display: isCollapsed ? 'none' : undefined,
                        justifyItems: 'center',
                    }}
                >
                    {children}
                </Stack>
            </Paper>
        </Resizable>
    );
};

const ExpandCollapseButtonIcons = ({
    side,
    collapsed,
    collapsedIcon,
}: { collapsed: boolean } & Pick<ResizableSidePanelProps, 'collapsedIcon' | 'side'>) => {
    if (side === 'right') {
        if (collapsed) {
            return (
                <>
                    <ChevronLeftIcon sx={{ ml: '-4px', mr: '-8px' }} />
                    {collapsedIcon}
                </>
            );
        }
        return <ChevronRightIcon sx={{ mx: '-4px' }} />;
    }
    // side === 'left'
    if (collapsed) {
        return (
            <>
                {collapsedIcon}
                <ChevronRightIcon sx={{ ml: '-8px', mr: '-4px' }} />
            </>
        );
    }
    return <ChevronLeftIcon sx={{ mx: '-4px' }} />;
};
